<script>
import Draggable from 'vuedraggable';
import ContentBlockMixin from '@shared/mixins/ContentBlock';
import { generateId } from '@shared/services/Utils';
import ContentBlockTimelineView from '@shared/components/ContentBlocks/Views/Timeline.vue';
import TiptapEditor from '@shared/components/TiptapEditor.vue';

export default {
  mixins: [ContentBlockMixin],
  components: {
    Draggable,
    ContentBlockTimelineView,
    TiptapEditor,
  },
  data: () => ({
    isForm: true,
  }),
  static: {
    data: {
      alignment: 'alternate',
      entries: [],
    },
  },
  methods: {
    addEntry() {
      this.newData.entries.push({
        id: generateId(),
        checkpoint: '',
        title: '',
        description: '',
        isFullWidth: false,
      });
    },
    removeEntry(entry) {
      this.newData.entries = this.newData
        .entries
        .filter((v) => v.id !== entry.id);
    },
  },
  created() {
    if (!this.newData.entries.length) {
      this.addEntry();
    }
  },
  i18n: {
    messages: {
      en: {
        timeline: {
          title: 'Create a timeline',
          entry_label: 'Entry',
          drag_handle_title: 'Click and drag to reorder',
          add_entry: 'Add Entry',
          no_entries: 'No timeline entries.',
          labels: {
            checkpoint: 'Checkpoint',
            title: 'Title (required if description is empty)',
            description: 'Description (required if title is empty)',
            alignment: 'Alignment',
            left: 'Left',
            right: 'Right',
            alternate: 'Alternate',
            is_full_width: 'Full width layout',
            is_full_width_tooltip: 'If checked, this entry will span the full width of the timeline.',
          },
        },
      },
      fr: {
        timeline: {
          title: 'Créer un fil chronologique',
          entry_label: 'Étape',
          drag_handle_title: 'Cliquez et déplacez pour réorganiser',
          add_entry: 'Ajouter une étape',
          no_entries: 'Aucune entrée dans le fil chronologique.',
          labels: {
            checkpoint: 'Étape',
            title: 'Titre (requis si la description est vide)',
            description: 'Description (requis si le titre est vide)',
            alignment: 'Position',
            left: 'Gauche',
            right: 'Droite',
            alternate: 'Centrée',
            is_full_width: 'Affichage en pleine largeur',
            is_full_width_tooltip: 'Si coché, cet événement s\'étendra sur toute la largeur.',
          },
        },
      },
    },
  },
};
</script>

<template>
  <ContentBlockBase>
    <template #form>
      <div>
        <h3 class="title is-5">
          {{ $t('timeline.title') }}
        </h3>

        <b-field :label-for="`alignment-${contentBlock.id}`" :label="$t('timeline.labels.alignment')">
          <b-radio
            v-model="newData.alignment"
            native-value="alternate"
            :id="`alignment-alternate-${contentBlock.id}`"
          >
            {{ $t('timeline.labels.alternate') }}
          </b-radio>
          <b-radio
            v-model="newData.alignment"
            native-value="left"
            :id="`alignment-left-${contentBlock.id}`"
          >
            {{ $t('timeline.labels.left') }}
          </b-radio>
          <b-radio
            v-model="newData.alignment"
            native-value="right"
            :id="`alignment-right-${contentBlock.id}`"
          >
            {{ $t('timeline.labels.right') }}
          </b-radio>
        </b-field>

        <hr>

        <Draggable
          class="block"
          v-model="newData.entries"
          handle=".handle"
        >
          <RenderlessToggle
            v-for="(entry, k) in newData.entries"
            :key="entry.id || k"
            :open="k === newData.entries.length - 1"
            v-slot="{ isOpen, toggle }"
          >
            <div class="box">
              <div
                class="is-flex is-align-items-center"
                :class="{ 'mb-4': isOpen }"
              >
                <b-button
                  type="is-ghost"
                  class="handle mr-2 tdecoration-none cursor-grab"
                  icon-left="grip-lines"
                  :title="$t('timeline.drag_handle_title')"
                />
                <span class="tag is-medium is-primary">
                  {{ $t('timeline.entry_label') }} #{{ k + 1 }}
                </span>
                <div class="ml-auto">
                  <b-button
                    v-show="newData.entries.length > 1"
                    type="is-danger"
                    icon-left="times"
                    outlined
                    @click="removeEntry(entry)"
                  />
                  <b-button
                    class="ml-2"
                    :icon-right="isOpen ? 'chevron-up' : 'chevron-down'"
                    @click="toggle"
                  />
                </div>
              </div>
              <div v-show="isOpen" class="columns is-multiline">
                <div class="column is-12">
                  <b-field
                    :label="$t('timeline.labels.checkpoint')"
                    :label-for="`checkpoint-${contentBlock.id}-${k}`"
                  >
                    <b-input
                      v-model.trim="entry.checkpoint"
                      :id="`checkpoint-${contentBlock.id}-${k}`"
                      required
                    />
                  </b-field>
                </div>
                <div class="column is-12">
                  <b-field
                    :label="$t('timeline.labels.title')"
                    :label-for="`title-${contentBlock.id}-${k}`"
                  >
                    <b-input
                      v-model.trim="entry.title"
                      :id="`title-${contentBlock.id}-${k}`"
                      :required="!entry.description"
                    />
                  </b-field>
                </div>
                <div class="column is-12">
                  <b-field
                    :label="$t('timeline.labels.description')"
                    :label-for="`description-${contentBlock.id}-${k}`"
                  >
                    <TiptapEditor
                      :value="entry.description"
                      @input="entry.description = $event"
                      :fieldId="`description-${contentBlock.id}-${k}`"
                    />
                  </b-field>
                </div>
                <div class="column is-12">
                  <b-field>
                    <b-checkbox
                      v-model="entry.isFullWidth"
                      :native-value="true"
                    >
                      {{ $t('timeline.labels.is_full_width') }}
                      <b-tooltip
                        :label="$t('timeline.labels.is_full_width_tooltip')"
                        multilined
                      >
                        <b-icon
                          icon="info-circle"
                          size="is-small"
                          class="ml-1"
                        />
                      </b-tooltip>
                    </b-checkbox>
                  </b-field>
                </div>
              </div>
            </div>
          </RenderlessToggle>
        </Draggable>

        <b-button @click="addEntry">
          {{ $t('timeline.add_entry') }}
        </b-button>

        <hr>
      </div>
    </template>
    <template #view>
      <ContentBlockTimelineView v-bind="viewProps" :data="data" />
    </template>
  </ContentBlockBase>
</template>

<style lang="scss" scoped>
.timeline {
  list-style: none;
  padding: 0;
  margin: 0;
  position: relative;
  border-left: 4px solid #dbdbdb;
  margin-left: 1rem;
}

.timeline-entry {
  position: relative;
  margin-bottom: 2rem;
  padding-left: 2rem;

  &:last-child {
    margin-bottom: 0;
  }

  &::before {
    content: '';
    background-color: white;
    border: 3px solid #3273dc;
    border-radius: 50%;
    position: absolute;
    left: -11px;
    top: 0;
    width: 20px;
    height: 20px;
    z-index: 1;
  }
}

.timeline-checkpoint {
  position: absolute;
  left: 2rem;
  top: -0.5rem;
  font-weight: bold;
  color: #3273dc;
  background: #fff;
  padding: 0 0.5rem;
  margin-left: -0.5rem;
}

.timeline-content {
  padding-top: 1.5rem;
}

.timeline-title {
  font-weight: 600;
  margin-bottom: 0.5rem !important;
}

.timeline-description {
  color: #555;
  margin-bottom: 0 !important;
}
</style>
